import { Component, model, input, computed, effect, untracked, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { InputText } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { DatePicker } from 'primeng/datepicker';
import { Checkbox } from 'primeng/checkbox';
import { Select } from 'primeng/select';
import { MultiSelect } from 'primeng/multiselect';
import { DataType, LineItemModel, TableRowData } from '../../../../models/template.model';
import { SpeechService } from '../../../../services/speech.service';
import { TaskManagementService } from '../../../../services/task-management.service';
import { ProjectTask } from '../../../../models/task-management.models';
import { UserService, UserModel } from '../../../../services/user.service';
import { ProjectService } from '../../../../services/project.service';
import { BlockScheduleModel } from '../../../../models/blockschedule.model';


@Component({
  selector: 'app-table-control',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    InputText,
    InputNumber,
    DatePicker,
    Checkbox,
    Select,
    MultiSelect
  ],
  template: `
    <div class="checklist-control-container">
      <div class="ctrl-container-hdr">
        <label class="checklist-control-label">
          {{ lineItem().name }}
          @if (lineItem().isRequired) {
            <span class="required-indicator">*</span>
          }
        </label>
        <button type="button" class="ctrl-speaker-btn" (click)="speakLabel()"
          [attr.aria-label]="'Read label: ' + lineItem().name">
          <i class="ri-volume-up-line"></i>
        </button>
      </div>
      <div class="checklist-control-input">
        <p-table
          [value]="rows()"
          [tableStyle]="{ 'min-width': '50rem' }"
          styleClass="p-datatable-sm"
        >
          <ng-template pTemplate="header">
            <tr>
              @for (column of columns(); track column.columnId) {
                <th>
                  <span class="th-label-wrap">
                    {{ column.columnName }}
                    @if (column.isRequired) {
                      <span class="required-indicator">*</span>
                    }
                  </span>
                </th>
              }
              <th style="width: 4rem"></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-rowData let-rowIndex="rowIndex">
            <tr>
              @for (column of columns(); track column.columnId) {
                <td [ngClass]="{'required-empty': shouldShowValidationError(rowIndex, column, rowData['col_' + column.columnId])}">
                  <div>
                    @switch (column.dataTypeId) {
                      <!-- Short Text -->
                      @case (1) {
                        <input
                          type="text"
                          pInputText
                          [(ngModel)]="rowData['col_' + column.columnId]"
                          (blur)="onCellBlur(rowIndex, column.columnId)"
                          (ngModelChange)="onCellValueChange()"
                          [disabled]="disabled()"
                          class="w-full"
                        />
                      }
                      <!-- Long Text -->
                      @case (2) {
                        <input
                          type="text"
                          pInputText
                          [(ngModel)]="rowData['col_' + column.columnId]"
                          (blur)="onCellBlur(rowIndex, column.columnId)"
                          (ngModelChange)="onCellValueChange()"
                          [disabled]="disabled()"
                          class="w-full"
                        />
                      }
                      <!-- Integer -->
                      @case (3) {
                        <p-inputnumber
                          [(ngModel)]="rowData['col_' + column.columnId]"
                          (onBlur)="onCellBlur(rowIndex, column.columnId)"
                          (ngModelChange)="onCellValueChange()"
                          [disabled]="disabled()"
                          [useGrouping]="false"
                          [min]="0"
                          styleClass="w-full"
                        />
                      }
                      <!-- Decimal -->
                      @case (4) {
                        <p-inputnumber
                          [(ngModel)]="rowData['col_' + column.columnId]"
                          (onBlur)="onCellBlur(rowIndex, column.columnId)"
                          (ngModelChange)="onCellValueChange()"
                          [disabled]="disabled()"
                          [minFractionDigits]="2"
                          [maxFractionDigits]="2"
                          [min]="0"
                          styleClass="w-full"
                        />
                      }
                      <!-- Date -->
                      @case (5) {
                        <p-datepicker
                          [(ngModel)]="rowData['col_' + column.columnId]"
                          (onHide)="onCellBlur(rowIndex, column.columnId)"
                          (ngModelChange)="onCellValueChange()"
                          [disabled]="disabled()"
                          dateFormat="yy-mm-dd"
                          [showIcon]="true"
                          iconDisplay="input"
                          [appendTo]="'body'"
                          styleClass="w-full"
                        />
                      }
                      <!-- CheckBox -->
                      @case (6) {
                        <p-checkbox
                          [(ngModel)]="rowData['col_' + column.columnId]"
                          (change)="onCellBlur(rowIndex, column.columnId)"
                          (ngModelChange)="onCellValueChange()"
                          [disabled]="disabled()"
                          [binary]="true"
                        />
                      }
                      <!-- Select -->
                      @case (7) {
                        <p-select
                          [(ngModel)]="rowData['col_' + column.columnId]"
                          (onChange)="onCellBlur(rowIndex, column.columnId)"
                          (ngModelChange)="onCellValueChange()"
                          [disabled]="disabled()"
                          [options]="getColumnOptions(column)"
                          optionLabel="valueText"
                          optionValue="valueText"
                          placeholder="Select an option..."
                          styleClass="w-full"
                          appendTo="body"
                        />
                      }
                      <!-- Tasks -->
                      @case (8) {
                        <p-select
                          [(ngModel)]="rowData['col_' + column.columnId]"
                          (onChange)="onCellBlur(rowIndex, column.columnId)"
                          (ngModelChange)="onCellValueChange()"
                          [disabled]="disabled() || loadingTasks()"
                          [options]="taskOptions()"
                          optionLabel="displayText"
                          optionValue="taskId"
                          [placeholder]="loadingTasks() ? 'Loading tasks...' : 'Select a task...'"
                          [filter]="true"
                          filterBy="displayText"
                          styleClass="w-full"
                          appendTo="body"
                        />
                      }
                      <!-- Users -->
                      @case (9) {
                        <p-multiselect
                          [(ngModel)]="rowData['col_' + column.columnId]"
                          (onChange)="onCellBlur(rowIndex, column.columnId)"
                          (ngModelChange)="onCellValueChange()"
                          [disabled]="disabled() || loadingUsers()"
                          [options]="userOptions()"
                          optionLabel="displayName"
                          optionValue="userId"
                          [placeholder]="loadingUsers() ? 'Loading users...' : 'Select users...'"
                          display="chip"
                          [filter]="true"
                          filterBy="displayName,email"
                          styleClass="w-full"
                          appendTo="body"
                          [scrollHeight]="'300px'"
                        />
                      }
                      <!-- DateTimePicker -->
                      @case (10) {
                        <p-datepicker
                          [(ngModel)]="rowData['col_' + column.columnId]"
                          (onHide)="onCellBlur(rowIndex, column.columnId)"
                          (ngModelChange)="onCellValueChange()"
                          [disabled]="disabled()"
                          dateFormat="yy-mm-dd"
                          [showTime]="true"
                          hourFormat="12"
                          [showIcon]="true"
                          iconDisplay="input"
                          [appendTo]="'body'"
                          styleClass="w-full"
                        />
                      }
                      <!-- Skids -->
                      @case (11) {
                        <p-select
                          [(ngModel)]="rowData['col_' + column.columnId]"
                          (onChange)="onCellBlur(rowIndex, column.columnId)"
                          (ngModelChange)="onCellValueChange()"
                          [disabled]="disabled() || loadingSkids()"
                          [options]="skidOptions()"
                          optionLabel="displayName"
                          optionValue="blockScheduleId"
                          [placeholder]="loadingSkids() ? 'Loading MV Power Stations...' : 'Select MV Power Station...'"
                          [filter]="true"
                          filterBy="displayName"
                          styleClass="w-full"
                          appendTo="body"
                        />
                      }
                    }
                    @if (shouldShowValidationError(rowIndex, column, rowData['col_' + column.columnId])) {
                      <small class="text-danger">{{ column.columnName }} is required</small>
                    }
                  </div>
                </td>
              }
              <td>
                <button type="button" class="ctrl-icon-btn ctrl-icon-btn--danger"
                  (click)="deleteRow(rowIndex)" [disabled]="disabled()" title="Delete row">
                  <i class="ri-delete-bin-line"></i>
                </button>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td [attr.colspan]="(columns() ?? []).length + 1" style="text-align: center; padding: 1rem;">
                No entries added. Click "+ Add entry" to add a row.
              </td>
            </tr>
          </ng-template>
        </p-table>
        @if (showTableRequiredError()) {
          <small class="text-danger">{{ lineItem().name }} is required</small>
        }
        <div class="mt-2">
          <button type="button" class="ctrl-ghost-btn" (click)="addRow()" [disabled]="disabled()">
            <i class="ri-add-line"></i>Add Entry
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .th-label-wrap {
      display: inline-flex;
      align-items: center;
      gap: 2px;
      white-space: nowrap;
    }
    .required-indicator {
      color: var(--color-error-mid);
      font-weight: bold;
      margin-left: 2px;
    }
    .checklist-control-container {
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 14px 16px;
      background: var(--color-white);
    }
    .text-danger {
      color: #ef4444;
      display: block;
      margin-top: 0.25rem;
      font-size: 0.875rem;
    }
    ::ng-deep .p-datatable .p-datatable-tbody > tr > td.required-empty {
      border: 2px solid #ef4444 !important;
      background-color: #fef2f2;
    }
    ::ng-deep .p-datatable .p-datatable-tbody > tr > td.required-empty input,
    ::ng-deep .p-datatable .p-datatable-tbody > tr > td.required-empty .p-inputnumber {
      border-color: #ef4444;
    }
  `]
})
export class TableControlComponent implements OnInit {
  lineItem = input.required<LineItemModel>();
  value = model<TableRowData[]>([]);
  disabled = input<boolean>(false);
  projectId = input<number>(0);
  showError = input<boolean>(false);

  // Get columns from line item
  columns = computed(() => this.lineItem().lineItemTableColumnList);

  // Rows getter/setter for template binding
  rows = computed(() => this.value());
  showTableRequiredError = computed(() =>
    this.showError() && this.lineItem().isRequired && (this.rows()?.length ?? 0) === 0
  );

  // Track which cells have been touched (rowIndex_columnId)
  touchedCells = new Set<string>();

  // Tasks support
  loadingTasks = signal<boolean>(false);
  taskOptions = signal<{ taskId: number; displayText: string }[]>([]);

  // Users support
  loadingUsers = signal<boolean>(false);
  userOptions = signal<{ userId: number; displayName: string; email: string }[]>([]);

  // Skids support
  loadingSkids = signal<boolean>(false);
  skidOptions = signal<{ blockScheduleId: number; displayName: string; tmeicSkidNo: number }[]>([]);

  private speechService = inject(SpeechService);
  private taskManagementService = inject(TaskManagementService);
  private userService = inject(UserService);
  private projectService = inject(ProjectService);

  constructor() {
    // Convert date strings to Date objects when value changes
    effect(() => {
      const rows = this.value();
      if (rows && rows.length > 0) {
        const columns = untracked(() => this.columns());
        const dateColumns = columns?.filter(col => col.dataTypeId === DataType.Date || col.dataTypeId === DataType.DateTimePicker);

        if (dateColumns??[].length > 0) {
          let hasChanges = false;
          const updatedRows = rows.map(row => {
            const newRow = { ...row };
            dateColumns?.forEach(col => {
              const key = `col_${col.columnId}`;
              const value = newRow[key];

              // Convert string dates to Date objects
              if (value && typeof value === 'string' && this.isISODateString(value)) {
                newRow[key] = new Date(value);
                hasChanges = true;
              }
            });
            return newRow;
          });

          // Only update if there were changes
          if (hasChanges) {
            untracked(() => this.value.set(updatedRows));
          }
        }
      }
    });
  }

  private isISODateString(value: string): boolean {
    // Check if string matches ISO 8601 date format
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;
    return isoDateRegex.test(value);
  }

  addRow(): void {
    const newRow: TableRowData = {};
    // Initialize each column with default value based on data type
    this.columns()?.forEach(column => {
      const key = `col_${column.columnId}`;
      switch (column.dataTypeId) {
        case DataType.Integer:
        case DataType.Decimal:
          newRow[key] = 0;
          break;
        case DataType.CheckBox:
          newRow[key] = false;
          break;
        case DataType.Date:
          newRow[key] = null;
          break;
        case DataType.DateTimePicker:
          newRow[key] = null;
          break;
        case DataType.Select:
          newRow[key] = '';
          break;
        case DataType.Tasks:
          newRow[key] = null;
          break;
        case DataType.Users:
          newRow[key] = [];
          break;
        case DataType.Skids:
          newRow[key] = null;
          break;
        default:
          newRow[key] = '';
      }
    });

    const currentRows = this.value() || [];
    const updatedRows = [...currentRows, newRow];
    this.value.set(updatedRows);
  }

  deleteRow(index: number): void {
    const currentRows = this.value() || [];
    const updatedRows = [...currentRows];
    updatedRows.splice(index, 1);
    this.value.set(updatedRows);

    // Clean up touched cells for deleted row and update indices for rows below
    const newTouchedCells = new Set<string>();
    this.touchedCells.forEach(key => {
      const [rowIdx, colId] = key.split('_').map(Number);
      if (rowIdx < index) {
        // Keep cells from rows before deleted row
        newTouchedCells.add(key);
      } else if (rowIdx > index) {
        // Decrement row index for rows after deleted row
        newTouchedCells.add(`${rowIdx - 1}_${colId}`);
      }
      // Skip cells from deleted row (rowIdx === index)
    });
    this.touchedCells = newTouchedCells;
  }

  onCellBlur(rowIndex: number, columnId: number | undefined): void {
    // Mark this cell as touched
    if (columnId === undefined) return;
    const key = `${rowIndex}_${columnId}`;
    this.touchedCells.add(key);
  }

  isCellTouched(rowIndex: number, columnId: number | undefined): boolean {
    if (columnId === undefined) return false;
    const key = `${rowIndex}_${columnId}`;
    return this.touchedCells.has(key);
  }

  shouldShowValidationError(rowIndex: number, column: any, value: any): boolean {
    // Show required cell errors after submit or after cell interaction.
    if (!column.isRequired) return false;
    if (!this.showError() && !this.isCellTouched(rowIndex, column.columnId)) return false;
    return this.isValueEmpty(value, column.dataTypeId);
  }

  onCellValueChange(): void {
    // Trigger model update when cell value changes
    // This ensures the ngClass binding re-evaluates and parent component gets notified
    const currentRows = this.value();
    this.value.set([...currentRows]); // Create new array reference to trigger change detection
  }

  getColumnOptions(column: any) {
    // Return the options for select columns
    return column.columnOptionValueList?.filter((opt: any) => opt.isActive !== false) || [];
  }

  isValueEmpty(value: any, dataTypeId?: number): boolean {
    if (value === null || value === undefined) {
      return true;
    }

    switch (dataTypeId) {
      case DataType.ShortText:
      case DataType.LongText:
        return typeof value === 'string' && value.trim() === '';
      case DataType.Integer:
      case DataType.Decimal:
        return value === 0 || value === '';
      case DataType.Date:
        return !value;
      case DataType.DateTimePicker:
        return !value;
      case DataType.CheckBox:
        return false; // Checkboxes are never considered empty (they're boolean)
      case DataType.Tasks:
        return !value || value === 0;
      case DataType.Users:
        return !value || !Array.isArray(value) || value.length === 0;
      case DataType.Skids:
        if (value === null || value === undefined || value === '') {
          return true;
        }
        const parsedSkid = Number(value);
        return !(Number.isFinite(parsedSkid) && (parsedSkid === -1 || parsedSkid > 0));
      default:
        return !value;
    }
  }

  validateRequiredColumns(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    const rows = this.value() || [];
    const columns = this.columns() || [];
    const requiredColumns = columns.filter(col => col.isRequired);

    if (requiredColumns.length === 0) {
      return { isValid: true, errors: [] };
    }

    // Check each row for required column values
    rows.forEach((row, rowIndex) => {
      requiredColumns.forEach(column => {
        const key = `col_${column.columnId}`;
        const value = row[key];

        if (this.isValueEmpty(value, column.dataTypeId)) {
          errors.push(`Row ${rowIndex + 1}: "${column.columnName}" is required`);
        }
      });
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  ngOnInit(): void {
    this.loadTasks();
    this.loadUsers();
    this.loadSkids();
  }

  private loadTasks(): void {
    const projectId = this.projectId();
    if (!projectId || projectId === 0) {
      console.warn('ProjectId is not available for table tasks column');
      return;
    }

    // Check if any column has Tasks data type
    const hasTasksColumn = this.columns()?.some(col => col.dataTypeId === DataType.Tasks);
    if (!hasTasksColumn) {
      return;
    }

    this.loadingTasks.set(true);
    this.taskManagementService.getTasksByProject(projectId).subscribe({
      next: (tasks: ProjectTask[]) => {
        // Transform tasks to dropdown options with format "title #taskId"
        const options = tasks.map(task => ({
          taskId: task.taskId,
          displayText: `${task.title} #${task.taskId}`
        }));
        this.taskOptions.set(options);
        this.loadingTasks.set(false);
      },
      error: (error) => {
        console.error('Error loading tasks for table column:', error);
        this.loadingTasks.set(false);
        this.taskOptions.set([]);
      }
    });
  }

  private loadUsers(): void {
    // Check if any column has Users data type
    const hasUsersColumn = this.columns()?.some(col => col.dataTypeId === DataType.Users);
    if (!hasUsersColumn) {
      return;
    }

    this.loadingUsers.set(true);
    const projectId = this.projectId();
    const usersRequest = projectId > 0
      ? this.userService.getProjectUserList(projectId)
      : this.userService.getUserList();

    usersRequest.subscribe({
      next: (users: UserModel[]) => {
        // Transform users to dropdown options (project users in project context, global users otherwise)
        const options = users
          .filter(user => user.isActive)
          .map(user => ({
            userId: user.userId!,
            displayName: `${user.firstName} ${user.lastName}`,
            email: user.email
          }));
        this.userOptions.set(options);
        this.loadingUsers.set(false);
      },
      error: (error) => {
        console.error('Error loading users for table column:', error);
        this.loadingUsers.set(false);
        this.userOptions.set([]);
      }
    });
  }

  private loadSkids(): void {
    // Check if any column has Skids data type
    const hasSkidsColumn = this.columns()?.some(col => Number(col.dataTypeId) === DataType.Skids);
    if (!hasSkidsColumn) {
      return;
    }

    const projectId = this.projectId();
    if (!projectId || projectId === 0) {
      this.skidOptions.set([]);
      return;
    }

    this.loadingSkids.set(true);
    this.projectService.getProjectBlockSchedule(projectId).subscribe({
      next: (skids: BlockScheduleModel[]) => {
        const options = (skids || [])
          .filter(s => Number.isFinite(Number(s.blockScheduleId)))
          .map(s => ({
            blockScheduleId: Number(s.blockScheduleId),
            displayName: (s.name || s.blockName || `MV Power Station #${s.tmeicSkidNo ?? ''}`).toString().trim(),
            tmeicSkidNo: Number.isFinite(Number(s.tmeicSkidNo)) ? Number(s.tmeicSkidNo) : Number.MAX_SAFE_INTEGER
          }))
          .sort((a, b) => {
            if (a.tmeicSkidNo !== b.tmeicSkidNo) {
              return a.tmeicSkidNo - b.tmeicSkidNo;
            }
            return a.blockScheduleId - b.blockScheduleId;
          });

        this.skidOptions.set([
          { blockScheduleId: -1, displayName: 'N/A', tmeicSkidNo: -1 },
          ...options
        ]);
        this.loadingSkids.set(false);
      },
      error: (error) => {
        console.error('Error loading skids for table column:', error);
        this.loadingSkids.set(false);
        this.skidOptions.set([]);
      }
    });
  }

  speakLabel(): void {
    this.speechService.speak(this.lineItem().name);
  }
}
